import React, {
  useEffect,
  useState,
  useCallback,
  FunctionComponent,
} from 'react';
import './pin.scss';
import { IPinCode } from '../../types/pin';

const pinRefs: any = {};
const pinPrefix = 'pinRef-';

export const PinInput: FunctionComponent<IPinCode> = ({
  handleInput,
  className,
  length = 6,
}) => {
  const classBase = 'custom-pin';
  const [value, setValue] = useState<string[]>([]);

  Array.from(value).forEach((e, i) => {
    pinRefs[`${pinPrefix}${i}`] = React.createRef();
  });

  const checkKeyPress = useCallback(
    (e) => {
      const { keyCode } = e;

      // When user presses delete or backspace remove the latest value
      if (keyCode === 8 || keyCode === 46) {
        const filledValues = value.filter((filledValue) => !!filledValue);

        filledValues.pop();

        for (let i = filledValues.length; i < length; i++) {
          filledValues.push('');
        }

        setValue(filledValues);
      }
    },
    [value],
  );

  const handleOnChange = (val: string, index: number) => {
    const re = /^(\d*\.)?\d+$/;

    // When user pastes full pin value
    // We want to fill out every pin input
    if (val?.length === length && re.test(val)) {
      return setValue(val.split(''));
    }
    // When user already typed 1 digit into this input
    // Then goes back and types another one over the previous
    // We want to keep only the last value and override the previous
    if (val?.length > 1) val = val[val.length - 1];

    if (re.test(val)) {
      setValue((oldValue) => oldValue.map((e, i) => (i !== index ? e : val)));
    }
    return true;
  };

  const handleBlur = () => {
    const filledValues = value.filter((filledValue) => !!filledValue);

    for (let i = 0; i < filledValues.length; i++) {
      pinRefs[`${pinPrefix}${i}`].current?.classList?.add('hasText');
    }

    for (let i = filledValues.length; i < length; i++) {
      pinRefs[`${pinPrefix}${i}`].current?.classList?.remove('hasText');
    }
  };

  const handleFocus = () => {
    const filledValues = value.filter((filledValue) => !!filledValue);

    if (filledValues.length && pinRefs[`pinRef-${filledValues.length}`]) {
      pinRefs[`pinRef-${filledValues.length}`].current?.focus();
    }
  };

  useEffect(() => {
    const startValue = [];

    if (length) {
      for (let i = 0; i < length; i += 1) {
        startValue.push('');
      }

      setValue(startValue);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', checkKeyPress);

    return () => {
      window.removeEventListener('keydown', checkKeyPress);
    };
  }, [checkKeyPress]);

  useEffect(() => {
    if (handleInput) {
      handleInput(value.join(''));
    }

    setTimeout(() => {
      handleFocus();
      handleBlur();
    }, 100);
  }, [value]);

  return (
    <div className={`${classBase} d-flex justify-content-center`}>
      {value.map((e, i) => (
        <input
          className={`
            f-s-16 pin-input ${length === 6 ? 'add-space' : ''} 
            ${className || ''}
          `}
          id={`${i}`}
          key={i}
          value={e}
          placeholder="0"
          onChange={(ev) => handleOnChange(ev.target.value, i)}
                // Input is disabled if previous input doesn't have value
                // Or if following input has value
          disabled={(!!i && !value[i - 1]) || !!value[i + 1]}
          type="text"
          maxLength={length}
          ref={pinRefs[`${pinPrefix}${i}`]}
          data-testid={classBase}
          inputMode="numeric"
          pattern="[0-9]*"
        />
      ))}
    </div>
  );
};
